import { forwardRef, useEffect, useRef, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { StyledContainer, StyledInput, StyledUnit } from './CountInput.styled';

interface ICountInputProps {
	value: number;
	onChange: (value: number) => void;
	min?: number;
	max?: number;
	unit?: string;
	height?: number;
	width?: number | string;
	fontSize?: number;
	disabled?: boolean;
}

const CountInput = forwardRef<HTMLInputElement, ICountInputProps>(
	(
		{
			value,
			onChange,
			min,
			max,
			unit,
			height = 40,
			width,
			fontSize = 16,
			disabled,
		},
		ref,
	) => {
		const innerRef = useRef<HTMLInputElement | null>(null);
		const [focused, setFocused] = useState(false);

		const setRefs = (node: HTMLInputElement | null) => {
			innerRef.current = node;
			if (typeof ref === 'function') {
				ref(node);
			} else if (ref) {
				ref.current = node;
			}
		};

		useEffect(() => {
			if (innerRef.current && document.activeElement !== innerRef.current) {
				setFocused(false);
			}
		}, [value]);

		const hasValue = value > 0;
		const showUnit = !!unit && !focused;

		const displayValue = value ? value.toLocaleString('ru-RU') : '0';

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const raw = e.target.value.replace(/\D/g, '');

			if (!raw) {
				onChange(0);
				return;
			}

			let parsed = parseInt(raw, 10);
			if (typeof max === 'number' && parsed > max) parsed = max;
			if (typeof min === 'number' && parsed < min) parsed = min;
			onChange(parsed);
		};

		return (
			<StyledContainer
				$height={height}
				$width={width}
				$fontSize={fontSize}
				$active={hasValue}
				$disabled={disabled}
				onMouseEnter={() => {
					if (!disabled) {
						playSound(Sound.LinkCursor);
					}
				}}
			>
				<StyledInput
					ref={setRefs}
					$fontSize={fontSize}
					value={displayValue}
					size={Math.max(1, displayValue.length)}
					onChange={handleChange}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					disabled={disabled}
					tabIndex={-1}
				/>
				{showUnit && <StyledUnit $active={hasValue}>{unit}</StyledUnit>}
			</StyledContainer>
		);
	},
);

export { CountInput };
